import { emptyListResponse } from "@/domain/core/utils/django"

const state = {
    scope: null,
    chargebacks: { ...emptyListResponse },
    chargeback: null,
    statistics: null,
    searchResults: null,
    searching: false,
    creating: false,
    exporting: false,
}

export const getters = {
    scope: state => state.scope,
    chargebacks: state => state.chargebacks,
    chargeback: state => state.chargeback,
    statistics: state => state.statistics,
    searchResults: state => state.searchResults,
    searching: state => state.searching,
    creating: state => state.creating,
    exporting: state => state.exporting,
}

export const mutations = {
    setScope(state, scope) {
        state.scope = scope
    },

    setChargebacks(state, data) {
        state.chargebacks = data
    },

    clearChargebacks(state) {
        state.chargebacks = { ...emptyListResponse }
    },

    setChargeback(state, data) {
        state.chargeback = data
    },

    clearChargeback(state) {
        state.chargeback = null
    },

    setStatistics(state, data) {
        state.statistics = data
    },

    setSearchResults(state, results) {
        state.searchResults = results
    },

    clearSearchResults(state) {
        state.searchResults = null
    },

    setSearching(state, searching) {
        state.searching = searching
    },

    setCreating(state, creating) {
        state.creating = creating
    },

    setExporting(state, exporting) {
        state.exporting = exporting
    },
}

export const actions = accountingService => ({
    async fetchChargebacks({ commit, state, dispatch }, { filters = {}, pagination = {}, sorting = {} } = {}) {
        if (!state.scope) return

        try {
            const data = await accountingService.fetchChargebacks(state.scope, filters, pagination, sorting)
            commit('setChargebacks', data)
        } catch (e) {
            commit('setChargebacks', { ...emptyListResponse })
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load chargebacks'
            dispatch('notifications/error', errorMsg, { root: true })
        }
    },

    async fetchChargeback({ commit, state, dispatch }, id) {
        if (!state.scope) {
            throw new Error('Scope not set')
        }

        commit('clearChargeback')
        try {
            const data = await accountingService.fetchChargeback(state.scope, id)
            commit('setChargeback', data)
        } catch (e) {
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load chargeback'
            dispatch('notifications/error', errorMsg, { root: true })
        }
    },

    async fetchStatistics({ commit, state, dispatch }, filters = {}) {
        if (!state.scope) return

        try {
            const data = await accountingService.fetchChargebackStatistics(state.scope, filters)
            commit('setStatistics', data)
        } catch (e) {
            commit('setStatistics', null)
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to load statistics'
            dispatch('notifications/error', errorMsg, { root: true })
        }
    },

    async exportChargebacks({ commit, dispatch }, filters = {}) {
        commit('setExporting', true)
        try {
            await accountingService.exportChargebacks(filters)
        } catch (e) {
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to export chargebacks'
            dispatch('notifications/error', errorMsg, { root: true })
        } finally {
            commit('setExporting', false)
        }
    },

    async searchOrders({ commit, dispatch }, email) {
        if (!email || email.length < 3) {
            commit('setSearchResults', null)
            return
        }

        commit('setSearching', true)
        try {
            const results = await accountingService.searchOrdersForChargeback(email)
            commit('setSearchResults', results)
        } catch (e) {
            commit('setSearchResults', null)
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to search orders'
            dispatch('notifications/error', errorMsg, { root: true })
        } finally {
            commit('setSearching', false)
        }
    },

    clearSearchResults({ commit }) {
        commit('clearSearchResults')
    },

    async createChargeback({ commit, dispatch }, data) {
        commit('setCreating', true)
        try {
            await accountingService.createChargeback(data)
            dispatch('notifications/success', 'Chargeback created successfully', { root: true })
            await dispatch('fetchChargebacks')
            commit('clearSearchResults')
            return true
        } catch (e) {
            const errorMsg = e?.response?.data?.detail || e?.message || 'Failed to create chargeback'
            dispatch('notifications/error', errorMsg, { root: true })
            return false
        } finally {
            commit('setCreating', false)
        }
    },
})

export default accountingService => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(accountingService)
})
